import type { PortalMetadataContext } from '@/modules/client-portal/metadata.api'
import { useMemo, useRef } from 'react'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'
import { getCurrentClientPortalContext } from '@/lib/api/client-portal-context'

import { isValidUUID } from '@scrambl/shared'
import {
  linkAbortSignals,
  SessionChangedError,
} from '@scrambl/shared/session-scope'

/** Bootstrap readers cannot depend on the layout they are responsible for loading. */
export function useClientPortalReadSession(
  entityId: string | null | undefined,
) {
  const workspace = useWorkspaceSession()
  const route = getCurrentClientPortalContext()?.entityId ?? null
  const identity = JSON.stringify([workspace.key, entityId, route])
  const current = useRef(identity)
  current.current = identity
  return useMemo(() => {
    const enabled =
      !!workspace.ownerUserId &&
      isValidUUID(workspace.ownerUserId) &&
      !!entityId &&
      isValidUUID(entityId) &&
      entityId === route
    const assertCurrent = () => {
      workspace.assertUnchanged()
      if (
        !enabled ||
        workspace.signal.aborted ||
        current.current !== identity ||
        getCurrentClientPortalContext()?.entityId !== entityId
      )
        throw new SessionChangedError()
    }
    return {
      enabled,
      ownerUserId: workspace.ownerUserId,
      key: {
        workspaceSession: workspace.key,
        portalEntityId: entityId ?? null,
      },
      assertCurrent,
      async run<T>(
        signal: AbortSignal,
        action: (context: PortalMetadataContext) => Promise<T>,
      ): Promise<T> {
        assertCurrent()
        const linked = linkAbortSignals([signal, workspace.signal])
        try {
          linked.signal.throwIfAborted()
          const value = await action({
            userId: workspace.ownerUserId!,
            entityId: entityId!,
            signal: linked.signal,
          })
          assertCurrent()
          linked.signal.throwIfAborted()
          return value
        } finally {
          linked.dispose()
        }
      },
    }
  }, [workspace, entityId, route, identity])
}
